/**
 * The output format a visitor arrived for.
 *
 * The static landings are format-specific — someone reading /audio-to-vtt/ is
 * there for VTT — so their links into the app carry `?format=vtt` and the
 * picker opens on it instead of Plain Text. Links already indexed from before
 * that param existed still carry the campaign slug (`ru_audio-to-vtt`), which
 * says the same thing, so it is read as a fallback.
 *
 * Like `?lang=`, an explicit URL signal beats the format saved from an earlier
 * visit: clicking a VTT page is a fresh statement of intent. But it is a
 * signal about the ARRIVAL, so it is spent on arrival — `takeIncomingFormat`
 * hands it over exactly once per page per tab. Reading it on every load meant
 * the campaign tag, which stays in the address for analytics, kept re-imposing
 * the landing's format on someone who had since chosen another one, and on
 * /convert (where nothing is stripped) a reload silently undid the choice.
 */
import type { OutputFormat } from '../types/transcription'
import { EXPORT_FORMATS } from './subtitleConvert'

export const FORMAT_PARAM = 'format'
/** The converter's own name for the same thing: /srt-to-vtt/ is about the
 * format it converts TO. */
export const TARGET_PARAM = 'to'

const USED_KEY = 'stt_url_format_used'
const USED_MAX = 10

function known(value: string | null | undefined): OutputFormat | null {
  if (!value) return null
  const fmt = value.toLowerCase()
  return EXPORT_FORMATS.some((f) => f.format === fmt) ? (fmt as OutputFormat) : null
}

/** What the address asks for, with no side effects. */
export function incomingFormat(search?: string): OutputFormat | null {
  const query = search ?? (typeof window === 'undefined' ? '' : window.location.search)
  try {
    const params = new URLSearchParams(query)
    // “…-to-srt”, with or without a locale prefix and whatever the input side is
    const fromCampaign = params.get('utm_campaign')?.match(/-to-([a-z0-9]+)$/)?.[1]
    return known(params.get(TARGET_PARAM)) ?? known(params.get(FORMAT_PARAM)) ?? known(fromCampaign)
  } catch {
    return null
  }
}

/** The arrival this address represents. Deliberately NOT the whole query: the
 * `format` param is stripped after it is read, while `utm_campaign` stays for
 * analytics, so keying on the campaign and the path is what makes a reload
 * recognisable as the same arrival.
 *
 * With no campaign there is nothing durable in the address to key on, and the
 * path alone made two hand-written links to the same page one arrival —
 * `/?format=vtt` followed by `/?format=ass` left the picker on VTT. There the
 * format IS the arrival. It is deliberately NOT part of the key when a
 * campaign is present: the param is stripped on the way in, so a reload falls
 * back to the campaign slug, and a link whose param and slug disagree would
 * otherwise read as a second arrival on every reload — the very bug this
 * exists to stop. */
function arrivalKey(search: string, path: string, wanted: string): string {
  try {
    const campaign = new URLSearchParams(search).get('utm_campaign') ?? ''
    return `${path}|${campaign}|${campaign ? '' : wanted}`
  } catch {
    return `${path}||${wanted}`
  }
}

function usedArrivals(): string[] {
  try {
    const raw = sessionStorage.getItem(USED_KEY)
    return raw ? raw.split('\n') : []
  } catch {
    return []
  }
}

function rememberArrival(key: string): void {
  try {
    const next = [...usedArrivals().filter((k) => k !== key), key].slice(-USED_MAX)
    sessionStorage.setItem(USED_KEY, next.join('\n'))
  } catch {
    // storage denied — the signal is then applied once per load, as before
  }
}

/**
 * The format the visitor arrived for, or null once this arrival has been
 * spent. Per tab (sessionStorage): a fresh visit from the same landing in a
 * new tab is a fresh statement of intent, a reload of the page they are
 * already on is not.
 */
export function takeIncomingFormat(search?: string, path?: string): OutputFormat | null {
  const query = search ?? (typeof window === 'undefined' ? '' : window.location.search)
  const where = path ?? (typeof window === 'undefined' ? '' : window.location.pathname)
  const wanted = incomingFormat(query)
  if (!wanted) return null

  const key = arrivalKey(query, where, wanted)
  if (usedArrivals().includes(key)) return null
  rememberArrival(key)
  return wanted
}
